import React, { useCallback, useEffect, useMemo, useState } from 'react'
import notify from 'src/utils/notify'

import { getSchemaKeyFromName } from '../schema-designer/utils'
import ListboxField from './listbox.field'
import TextField from './text.field'

interface AttributeToAssociatedAttributeMap {
  [attribute: string]: number
}
interface AssociatedAttributeToAttributeMap {
  [code: string]: string
}
interface MappingGroup {
  [key: string]: AttributeToAssociatedAttributeMap | AssociatedAttributeToAttributeMap
}
interface Mapping {
  [category: string]: MappingGroup
}
type LookupTableFromMappingProps = {
  data: Mapping
  form: any
}

export function LookupTableFromMapping({ data: mappings, form }: LookupTableFromMappingProps) {
  const [localData, setLocalData] = useState<any>(mappings)
  const [selectedTable, setSelectedTable] = useState(form.getFieldValue('mappingTable'))
  const [showCreateInput, setShowCreateInput] = useState(true)
  const [newTableName, setNewTableName] = useState('')
  const [associatedAttribute, setAssociatedAttribute] = useState(form.getFieldValue('associatedAttribute'))
  const [isAttributeMatch, setIsAttributeMatch] = useState(false)

  const currentField = useMemo(() => getSchemaKeyFromName({ name: form.getFieldValue('title') }), [form])

  useEffect(() => {
    if (mappings && typeof mappings === 'object') setLocalData({ ...mappings })
  }, [mappings])

  useEffect(() => {
    if (selectedTable && localData[selectedTable]) {
      const keys = Object.keys(localData[selectedTable])
      const valueKey = keys.find((k) => typeof localData[selectedTable][k] === 'object') || ''
      const metaKey = keys.find((k) => k !== valueKey) || ''
      if (currentField === metaKey) {
        setAssociatedAttribute(valueKey)
        form.setFieldValue('associatedAttribute', valueKey)
        setIsAttributeMatch(false)
      } else if (currentField === valueKey) {
        setAssociatedAttribute(metaKey)
        form.setFieldValue('associatedAttribute', metaKey)
        setIsAttributeMatch(false)
      } else if (Object.keys(mappings).length > 0) {
        setIsAttributeMatch(true)
      }
      form.setFieldValue('mappingTable', selectedTable)
    }
  }, [selectedTable, localData, currentField])

  useEffect(() => {
    if (typeof form?.setValue === 'function') {
      form.setValue(`fields.${form.selectedField}.computed.mapping`, localData)
    }
  }, [localData, form])

  const saveLookupTable = useCallback(
    (tableName: string, keyValuePairs: { key: string; value: string }[]) => {
      const referenceField = getSchemaKeyFromName({ name: form.getFieldValue('title') })
      const associatedField = getSchemaKeyFromName({ name: form.getFieldValue('associatedAttribute') })
      const updated = { ...localData }
      updated[tableName] = {}
      updated[tableName][associatedField] = keyValuePairs.reduce(
        (acc, pair) => {
          acc[pair.key] = pair.value
          return acc
        },
        {} as Record<string, string>,
      )
      updated[tableName][referenceField] = keyValuePairs.reduce(
        (acc, pair) => {
          acc[pair.value] = pair.key
          return acc
        },
        {} as Record<string, string>,
      )
      form.setFieldValue('mappings', updated)
      setLocalData(updated)
    },
    [form, localData],
  )

  const handleDeleteRow = useCallback(
    (tableName: string, key: string) => {
      const updated = { ...localData }
      const table = updated[tableName] || {}
      const keys = Object.keys(table)
      const reverseKey = getSchemaKeyFromName({ name: form.getFieldValue('title') })
      const forwardKey = keys.find((k) => k !== reverseKey) || ''
      const forwardMap = { ...table[forwardKey] }
      const reverseMap = { ...table[reverseKey] }
      const value = forwardMap[key]
      delete forwardMap[key]
      if (reverseMap[value] === key) delete reverseMap[value]
      updated[tableName][forwardKey] = forwardMap
      updated[tableName][reverseKey] = reverseMap
      form.setFieldValue('mappings', updated)
      setLocalData(updated)
    },
    [form, localData],
  )

  // Helper for duplicate key check
  const hasDuplicateKeys = (pairs: { key: string; value: string }[]) => {
    const keySet = new Set<string>()
    for (const pair of pairs) {
      if (keySet.has(pair.key)) return true
      keySet.add(pair.key)
    }
    return false
  }

  // Helper for duplicate value check
  const hasDuplicateValues = (pairs: { key: string; value: string }[]) => {
    const valueSet = new Set<string>()
    for (const pair of pairs) {
      console.log(pair)
      if (valueSet.has(pair.value)) return true
      valueSet.add(pair.value)
    }
    return false
  }

  // Render key-value rows (extract as component for clarity)
  const KeyValueRows = ({ tableName }: { tableName?: string }) => {
    const [newKey, setNewKey] = useState('')
    const [newValue, setNewValue] = useState('')

    if (!localData || !localData[tableName || '']) {
      return (
        <>
          {associatedAttribute &&
            getSchemaKeyFromName({ name: currentField }) === getSchemaKeyFromName({ name: associatedAttribute }) && (
              <p className='mb-2 text-sm text-red-500'>Associated attribute's & attribute's name should not same.</p>
            )}
          {associatedAttribute && currentField !== associatedAttribute && (
            <>
              <div className='mb-2 flex w-full items-center gap-4'>
                <label className='text-light block w-[50%] text-left text-sm'>
                  {getSchemaKeyFromName({ name: associatedAttribute })}:
                </label>
                <label className='text-light block w-[50%] text-left text-sm'>
                  {getSchemaKeyFromName({ name: form.getFieldValue('title') })}:
                </label>
              </div>
              <div className='mb-2 flex items-center gap-4'>
                <TextField
                  placeholder='Key...'
                  name={`key${selectedTable || 'custom'}0`}
                  label=''
                  form={form}
                  value={newKey}
                  wrapperClassName='!mb-0'
                  onChange={(val?: string) => setNewKey(val ?? '')}
                />
                <TextField
                  placeholder='Value...'
                  name={`val${selectedTable || 'custom'}0`}
                  label=''
                  form={form}
                  value={newValue}
                  wrapperClassName='!mb-0'
                  onChange={(val?: string) => setNewValue(val ?? '')}
                />
                <button
                  type='button'
                  className='text-brand mt-2 inline-block text-sm font-bold'
                  onClick={() => {
                    if (selectedTable && newKey && newValue && selectedTable && associatedAttribute) {
                      const local = { ...localData }
                      const existingKeys = local[selectedTable]?.[associatedAttribute]
                        ? Object.keys(local[selectedTable][associatedAttribute])
                        : []
                      const existingValues = local[selectedTable]?.[associatedAttribute]
                        ? Object.values(local[selectedTable][associatedAttribute])
                        : []
                      if (existingKeys.includes(newKey)) {
                        notify.error('Duplicate keys are not allowed. Please ensure all keys are unique.')
                        return
                      }
                      if (existingValues.includes(newValue)) {
                        notify.error('Duplicate values are not allowed. Please ensure all values are unique.')
                        return
                      }
                      if (!local[selectedTable]) local[selectedTable] = { [associatedAttribute]: {} }
                      if (!local[selectedTable][associatedAttribute]) local[selectedTable][associatedAttribute] = {}
                      local[selectedTable][associatedAttribute] = {
                        ...local[selectedTable][associatedAttribute],
                        [newKey]: newValue,
                      }
                      setLocalData(local)
                      setNewKey('')
                      setNewValue('')
                      form.setFieldValue(`key${selectedTable}0`, '')
                      form.setFieldValue(`val${selectedTable}0`, '')
                      // Then, collect all key-value pairs and save the lookup table
                      const keyValuePairs: { key: string; value: string }[] = []
                      const entries = Object.entries(
                        local[selectedTable][form.getFieldValue('associatedAttribute')] || {},
                      )

                      entries.forEach(([key, value], idx) => {
                        const updatedKey = form.getFieldValue(`key${idx}`) ?? key
                        const updatedValue = form.getFieldValue(`val${idx}`) ?? value
                        keyValuePairs.push({ key: updatedKey, value: updatedValue as string })
                      })

                      // Add the new key-value pair if it was just added above
                      const newKeyField = form.getFieldValue(`key${selectedTable}0`)
                      const newValueField = form.getFieldValue(`val${selectedTable}0`)
                      if (newKeyField && newValueField) {
                        keyValuePairs.push({ key: newKeyField, value: newValueField })
                      }

                      // Validate for duplicates
                      if (hasDuplicateKeys(keyValuePairs)) {
                        notify.error('Duplicate keys are not allowed. Please ensure all keys are unique.')
                        return
                      }

                      if (hasDuplicateValues(keyValuePairs)) {
                        notify.error('Duplicate values are not allowed. Please ensure all values are unique.')
                        return
                      }

                      // Save the lookup table if no duplicates found
                      saveLookupTable(selectedTable, keyValuePairs)
                    }
                  }}
                >
                  Add row
                </button>
              </div>
            </>
          )}
        </>
      )
    }

    const entries = Object.entries(localData[tableName!][form.getFieldValue('associatedAttribute')] || {})
    return (
      <>
        {entries.map(([key, val], idx) => (
          <React.Fragment key={`${tableName}-${idx}`}>
            <div className='mb-2 flex w-full items-center gap-4'>
              {idx === 0 && (
                <label className='text-light block w-[47%] text-left text-sm'>
                  {getSchemaKeyFromName({ name: form.getFieldValue('associatedAttribute') })}:
                </label>
              )}
              {idx === 0 && (
                <label className='text-light block w-[50%] text-left text-sm'>
                  {getSchemaKeyFromName({ name: form.getFieldValue('title') })}:
                </label>
              )}
            </div>
            <div className='mb-2 flex items-center gap-4'>
              <TextField
                name={`key${idx}`}
                label=''
                form={form}
                placeholder='Key...'
                wrapperClassName='!mb-0'
                defaultValue={key}
                onBlur={(e) => {
                  const newKey = e.target.value.trim()
                  if (newKey !== key && newKey && tableName && associatedAttribute) {
                    const local = { ...localData }
                    const associatedAttr = form.getFieldValue('associatedAttribute')

                    if (local[tableName] && local[tableName][associatedAttr]) {
                      // Check for duplicate keys (excluding current key)
                      const existingKeys = Object.keys(local[tableName][associatedAttr] || {}).filter((k) => k !== key)
                      if (existingKeys.includes(newKey)) {
                        notify.error('Duplicate keys are not allowed. Please ensure all keys are unique.')
                        e.target.value = key // Reset to original value
                        return
                      }

                      // Get current value for this key
                      const currentValue = local[tableName][associatedAttr][key]

                      // Remove old key entry
                      delete local[tableName][associatedAttr][key]
                      // Add new key entry
                      local[tableName][associatedAttr][newKey] = currentValue

                      // Update reverse mapping
                      const reverseKey = getSchemaKeyFromName({ name: form.getFieldValue('title') })
                      if (local[tableName][reverseKey] && local[tableName][reverseKey][currentValue] === key) {
                        delete local[tableName][reverseKey][currentValue]
                        local[tableName][reverseKey][currentValue] = newKey
                      }

                      setLocalData(local)
                      form.setFieldValue('mappings', local)
                    }
                  }
                }}
              />
              <TextField
                name={`val${idx}`}
                label=''
                form={form}
                placeholder='Value...'
                wrapperClassName='!mb-0'
                defaultValue={val as string}
                onBlur={(e) => {
                  const newValue = e.target.value.trim()
                  if (newValue !== val && newValue && tableName && associatedAttribute) {
                    const local = { ...localData }
                    const associatedAttr = form.getFieldValue('associatedAttribute')

                    if (local[tableName] && local[tableName][associatedAttr]) {
                      // Check for duplicate values (excluding current value)
                      const existingValues = Object.values(local[tableName][associatedAttr] || {}).filter(
                        (v) => v !== val,
                      )
                      if (existingValues.includes(newValue)) {
                        notify.error('Duplicate values are not allowed. Please ensure all values are unique.')
                        e.target.value = val as string // Reset to original value
                        return
                      }

                      // Update the value for current key
                      local[tableName][associatedAttr][key] = newValue

                      // Update reverse mapping
                      const reverseKey = getSchemaKeyFromName({ name: form.getFieldValue('title') })
                      if (local[tableName][reverseKey]) {
                        // Remove old reverse mapping
                        delete local[tableName][reverseKey][val as string]
                        // Add new reverse mapping
                        local[tableName][reverseKey][newValue] = key
                      }

                      setLocalData(local)
                      form.setFieldValue('mappings', local)
                    }
                  }
                }}
              />
              <button
                type='button'
                onClick={() => handleDeleteRow(tableName!, key)}
                className='ml-2 text-xl font-bold text-gray-600 hover:text-red-500'
                id={`deleteRow${idx}`}
              >
                ×
              </button>
            </div>
          </React.Fragment>
        ))}
        <div className='mb-2 flex items-center gap-4' key={`new-row-1`}>
          <TextField
            name={`newKey${tableName}`}
            label=''
            form={form}
            placeholder='Key...'
            wrapperClassName='!mb-0'
            value={newKey}
            onChange={(val?: string) => setNewKey(val ?? '')}
          />
          <TextField
            name={`newVal${tableName}`}
            label=''
            form={form}
            placeholder='Value...'
            wrapperClassName='!mb-0'
            value={newValue}
            onChange={(val?: string) => setNewValue(val ?? '')}
          />
          <button
            type='button'
            className='text-brand ml-2 text-xl font-bold'
            onClick={() => {
              if (selectedTable) {
                // First, handle adding new key-value pair if provided
                if (newKey && newValue && associatedAttribute) {
                  const local = { ...localData }

                  const existingKeys = local[selectedTable]?.[associatedAttribute]
                    ? Object.keys(local[selectedTable][associatedAttribute])
                    : []
                  const existingValues = local[selectedTable]?.[associatedAttribute]
                    ? Object.values(local[selectedTable][associatedAttribute])
                    : []

                  if (existingKeys.includes(newKey)) {
                    notify.error('Duplicate keys are not allowed. Please ensure all keys are unique.')
                    return
                  }

                  if (existingValues.includes(newValue)) {
                    notify.error('Duplicate values are not allowed. Please ensure all values are unique.')
                    return
                  }

                  // Add the new key-value pair to local data
                  if (!local[selectedTable]) local[selectedTable] = { [associatedAttribute]: {} }
                  if (!local[selectedTable][associatedAttribute]) local[selectedTable][associatedAttribute] = {}
                  local[selectedTable][associatedAttribute] = {
                    ...local[selectedTable][associatedAttribute],
                    [newKey]: newValue,
                  }
                  setLocalData(local)
                  setNewKey('')
                  setNewValue('')
                  form.setFieldValue(`newKey${tableName}`, '')
                  form.setFieldValue(`newVal${tableName}`, '')
                }

                // Then, collect all key-value pairs and save the lookup table
                const keyValuePairs: { key: string; value: string }[] = []
                const entries = Object.entries(
                  localData[selectedTable][form.getFieldValue('associatedAttribute')] || {},
                )

                entries.forEach(([key, value], idx) => {
                  const updatedKey = key
                  const updatedValue = value
                  keyValuePairs.push({ key: updatedKey, value: updatedValue as string })
                })

                // Add the new key-value pair if it was just added above
                const newKeyField = form.getFieldValue(`newKey${selectedTable}1`)
                const newValueField = form.getFieldValue(`newVal${selectedTable}1`)
                if (newKeyField && newValueField) {
                  keyValuePairs.push({ key: newKeyField, value: newValueField })
                }

                // Validate for duplicates
                if (hasDuplicateKeys(keyValuePairs)) {
                  notify.error('Duplicate keys are not allowed. Please ensure all keys are unique.')
                  return
                }

                if (hasDuplicateValues(keyValuePairs)) {
                  notify.error('Duplicate values are not allowed. Please ensure all values are unique.')
                  return
                }

                // Save the lookup table if no duplicates found
                saveLookupTable(selectedTable, keyValuePairs)
              }
            }}
          >
            +
          </button>
        </div>
      </>
    )
  }

  return (
    <div className='max-w-4xl rounded-xl border border-gray-200 bg-gray-50'>
      <div className='border-b p-6'>
        {localData && Object.keys(localData).length > 0 && !showCreateInput ? (
          <div className='mb-4'>
            <ListboxField
              name='tableSelector'
              label='Choose lookup table'
              description='Choose an existing lookup table for this entity.'
              form={form}
              defaultValue={selectedTable || ''}
              options={Object.keys(localData).map((key) => ({ label: key, value: key }))}
              onChange={(val?: string) => setSelectedTable(val ?? '')}
            />
            <button
              type='button'
              className='text-brand mt-2 text-sm font-bold'
              onClick={() => {
                setShowCreateInput(true)
                setSelectedTable(null)
              }}
            >
              Create new table
            </button>
          </div>
        ) : (
          <div className='mb-4'>
            <TextField
              id='mappingTable'
              name='mappingTable'
              label='Lookup table name'
              placeholder='Lookup table name'
              description='Give your new lookup table a name. You can reuse this lookup for bidirectional mapping.'
              form={form}
              value={newTableName}
              onChange={(val?: string) => {
                setNewTableName(val ?? '')
                setSelectedTable(val ?? '')
              }}
            />
            {localData && typeof localData === 'object' && Object.keys(localData).length > 0 && (
              <button
                type='button'
                className='text-brand mt-2 block text-sm font-bold'
                onClick={() => {
                  setShowCreateInput(false)
                  setNewTableName('')
                  setAssociatedAttribute('')
                  setSelectedTable(null)
                }}
              >
                Use existing lookup table
              </button>
            )}
          </div>
        )}
      </div>
      <div className='p-6'>
        {selectedTable && localData && localData[selectedTable] ? (
          <div>
            {isAttributeMatch && (
              <p className='mb-2 text-sm text-red-500'>
                Property name "{currentField}" does not exist in the selected lookup table.
                <br />
                Possible values are:{' '}
                {Object.keys(localData[selectedTable])
                  .map((key) => `"${key}"`)
                  .join(', ')}
              </p>
            )}
            {associatedAttribute && (
              <TextField
                name='associatedAttribute'
                placeholder='Associate attribute'
                label='Associated attribute'
                description='Which other attribute from this entity determines the value for this attribute?'
                form={form}
                disabled
              />
            )}
            {!associatedAttribute && (
              <TextField
                name='associatedAttribute'
                label='Associated attribute'
                description='Which other attribute from this entity determines the value for this attribute?'
                form={form}
                placeholder='Associate attribute'
                onChange={(val?: string) => {
                  form.setFieldValue('associatedAttribute', val ?? '')
                }}
              />
            )}
            {!isAttributeMatch && <KeyValueRows tableName={selectedTable} />}
          </div>
        ) : (
          <div key={selectedTable}>
            <TextField
              name='associatedAttribute'
              label='Associated attribute'
              description='Which other attribute from this entity determines the value for this attribute?'
              placeholder='Associate attribute'
              form={form}
              onChange={(val?: string) => {
                form.setFieldValue('associatedAttribute', val ?? '')
                setAssociatedAttribute(val ?? '')
              }}
            />
            <KeyValueRows tableName={selectedTable ?? ''} />
          </div>
        )}
      </div>
    </div>
  )
}

function LookupTableWrapper({ form }: { form: any }) {
  const [mappings, setMappings] = useState<Mapping | null>(form.getFieldValue('mappings') || null)
  const [isLoading, setIsLoading] = useState(!form.getFieldValue('mappings'))
  useEffect(() => {
    if (!form.getFieldValue('mappings')) {
      setTimeout(() => {
        setMappings({})
        setIsLoading(false)
      }, 1000)
    }
  }, [form])
  if (isLoading) return <div className='p-4'>Loading mappings...</div>
  if (!mappings) return <div className='p-4 text-gray-500'>No mappings available.</div>
  return <LookupTableFromMapping data={mappings} form={form} />
}

export default LookupTableWrapper
